import { baseService } from "src/store/base.store";
import { RewardExchange } from "./types/karma-exchange.entity";
import { executeQuery } from "src/store";
import { karmaExchangesRepository } from "./repositories/karma-exchanges.repository";
import { userRepository } from "../users/repositories/user.repository";
import { rewardsRepository } from "../rewards/repositories/rewards.repository";
import { callFunctionSafe } from "src/firebase/functions";
import { User } from "../users/types/user.entity";
import { Reward } from "../rewards/types/reward.entity";
import { requestedCountriesRepository } from "./repositories/requested-countries.repository";
import { KarmaExchangeRequestedCountry } from "./types/requested-country.entity";
import { Address } from "src/entities/user-address.entity";
import { createUserAddressRepository } from "../users/repositories/user-address.repository";

export type PopulatedRewardExchange = RewardExchange & {
  user?: User | null;
  reward?: Reward | null;
};

export type PopulatedRewardExchangeDetails = PopulatedRewardExchange & {
  address?: Address | null;
};

export type PopulatedRequestedCountry = KarmaExchangeRequestedCountry & {
  user?: User | null;
};

type RewardExchangeStatus = NonNullable<RewardExchange["status"]>;

export const karmaExchangeService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getKarmaExchanges: builder.query<PopulatedRewardExchange[], { where?: any; offset?: string }>({
      queryFn: async ({ where = {}, offset }) =>
        executeQuery(async () => {
          const exchanges = await karmaExchangesRepository.find({
            where,
            offset,
            orderBy: ["createdAt", "desc"],
          });
          
          const populatedExchanges = await Promise.all(
            exchanges.map(async (exchange) => {
              const user = await userRepository.findById(exchange.userId);
              const reward = await rewardsRepository.findById(exchange.rewardId);
              return {
                ...exchange,
                user,
                reward,
              };
            })
          );
          
          return populatedExchanges;
        }),
      providesTags: ["KarmaExchanges"],
    }),
    countKarmaExchanges: builder.query<number, void>({
      queryFn: async () => executeQuery(() => karmaExchangesRepository.count()),
    }),
    getKarmaExchangeById: builder.query<PopulatedRewardExchangeDetails | null, string>({
      queryFn: async (exchangeId) =>
        executeQuery(async () => {
          const exchange = await karmaExchangesRepository.findById(exchangeId);

          if (!exchange) {
            return null;
          }

          const [user, reward] = await Promise.all([
            userRepository.findById(exchange.userId),
            rewardsRepository.findById(exchange.rewardId),
          ]);

          let address: Address | null = null;
          const rewardCategory = reward?.category || exchange.category;

          if (rewardCategory === "PHISICAL" && exchange.addressId) {
            const addressRepository = createUserAddressRepository(exchange.userId);
            address = await addressRepository.findById(exchange.addressId);
          }

          return {
            ...exchange,
            user,
            reward,
            address,
          };
        }),
      providesTags: ["KarmaExchanges"],
    }),
    getKarmaExchangeRequestedCountries: builder.query<
      PopulatedRequestedCountry[],
      { where?: any; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) =>
        executeQuery(async () => {
          const requests = await requestedCountriesRepository.find({
            where,
            offset,
            limit,
            orderBy: ["createdAt", "desc"],
          });

          const userIds = Array.from(new Set(requests.map((request) => request.userId).filter(Boolean)));
          const users = await userRepository.findByIds(userIds);
          const usersMap = new Map(users.map((user) => [user.id, user]));

          return requests.map((request) => ({
            ...request,
            user: usersMap.get(request.userId) || null,
          }));
        }),
      providesTags: ["KarmaExchanges"],
    }),
    countKarmaExchangeRequestedCountries: builder.query<number, { where?: any } | void>({
      queryFn: async (arg) => executeQuery(() => requestedCountriesRepository.count(arg && "where" in arg ? arg.where : undefined)),
    }),
    countUnseenKarmaExchangeRequestedCountries: builder.query<number, void>({
      queryFn: async () => executeQuery(() => requestedCountriesRepository.countUnseen()),
      providesTags: ["KarmaExchanges"],
    }),
    markKarmaExchangeRequestedCountriesSeen: builder.mutation<void, void>({
      queryFn: async () => executeQuery(() => requestedCountriesRepository.markAllUnseenAsSeen()),
      invalidatesTags: ["KarmaExchanges"],
    }),
    updateRewardExchangeStatus: builder.mutation<void, { exchangeId: string; newStatus: RewardExchangeStatus }>({
      queryFn: async ({ exchangeId, newStatus }) =>
        callFunctionSafe("updateRewardExchangeStatus", {
          exchangeId,
          newStatus,
        }),
      invalidatesTags: ["KarmaExchanges"],
    }),
    processKarmaExchange: builder.mutation<void, { id: string; action: "APPROVE" | "REJECT" }>({
      queryFn: async ({ id, action }) =>
        callFunctionSafe("updateRewardExchangeStatus", {
          exchangeId: id,
          newStatus: (action === "APPROVE" ? "PROCESSING" : "REJECTED") as RewardExchangeStatus,
        }),
      invalidatesTags: ["KarmaExchanges"],
    }),
  }),
});

export const {
  useGetKarmaExchangesQuery,
  useCountKarmaExchangesQuery,
  useGetKarmaExchangeByIdQuery,
  useGetKarmaExchangeRequestedCountriesQuery,
  useCountKarmaExchangeRequestedCountriesQuery,
  useCountUnseenKarmaExchangeRequestedCountriesQuery,
  useMarkKarmaExchangeRequestedCountriesSeenMutation,
  useUpdateRewardExchangeStatusMutation,
  useProcessKarmaExchangeMutation,
} = karmaExchangeService;
